'use client'

import useNotificationMenu from '@/app/core/components/notification/hooks/useNotificationMenu'
import NotificationMenu from '@/app/core/components/notification/components/NotificationMenu'
import NotificationContainer from '@/app/core/components/notification/components/NotificationContainer'
import NotificationaUserAvatars from '@/app/core/components/notification/NotificationUserAvatars'
import NotificationResolvedUserName from '@/app/core/components/notification/NotificationResolvedUserName'
import {
  isNotificationUnread,
  isUserUnassignedCustomNotification,
  parseRoomId,
} from '@/app/core/components/notification/utils'
import { useResolveRooms } from '@/dc-shared-web/src'
import { InboxNotificationCustomData } from '@liveblocks/core'

const getNotificationContent = (
  checklistName: string,
  sectionName: string | undefined
) => {
  let target

  if (sectionName) {
    target = (
      <>
        <span className='tw-font-medium'>{sectionName}</span> -{' '}
        <span className='tw-font-medium'>{checklistName}</span>
      </>
    )
  } else {
    target = (
      <>
        <span className='tw-font-medium'>{checklistName}</span>
      </>
    )
  }

  return {
    action: 'has been removed from',
    target,
  }
}

// Extract data specific to assignment notifications
export const useAssignmentNotificationDetails = (
  notification: InboxNotificationCustomData<'$userAssignmentRemoved'>
) => {
  const activityData = notification.activities[0].data
  const { crunchUuid, checklistUuid } = parseRoomId(notification.roomId)

  // Format the notification URL
  const notificationUrl = `/crunches/${crunchUuid}?infoPanel=${
    activityData.assignmentType === 'checklist' ? 'Checklist' : 'Cheatsheet'
  }${activityData.assignmentType === 'checklist' ? `&checklist=${checklistUuid}` : ''}${
    activityData.sectionUuid ? `&section=${activityData.sectionUuid}` : ''
  }`

  const roomId = notification.roomId || ''
  // Use the useResolveRooms hook to get room information efficently
  const { data: roomsData, isLoading: isLoadingRooms } = useResolveRooms([
    roomId,
  ])

  return {
    assignmentType: activityData.assignmentType,
    sectionUuid: activityData.sectionUuid,
    sectionName: activityData.sectionName,
    notificationUrl,
    removedUserUuid: activityData.removedUserUuid,
    roomId,
    roomInfo: roomsData && roomsData[0],
    isLoadingRoomInfo: isLoadingRooms,
  }
}

const NotificationContent = ({
  removedUserUuid,
  crunchName,
  checklistName,
  sectionName,
}: {
  removedUserUuid: string
  crunchName: string
  checklistName: string
  sectionName: string | undefined
  isUnassigned: boolean
}) => {
  const { action, target } = getNotificationContent(checklistName, sectionName)

  return (
    <div>
      <NotificationResolvedUserName
        userId={removedUserUuid}
        className='tw-font-medium'
      />{' '}
      {action} {target}
      {' in '}
      <span className='tw-font-medium'>{crunchName}</span>
      {' as they are no longer available.'}
    </div>
  )
}

export default function AssignmentRemovedNotification({
  notification,
  onClick,
}: {
  notification: InboxNotificationCustomData<'$userAssignmentRemoved'>
  onClick: () => void
}) {
  const {
    notificationUrl,
    removedUserUuid: triggerUser,
    isLoadingRoomInfo,
    sectionName,
    roomInfo,
  } = useAssignmentNotificationDetails(notification)

  const isUnread = isNotificationUnread(notification)

  const {
    isHovered,
    isMenuOpened,
    setIsMenuOpened,
    onMarkAsRead,
    onDelete,
    onMouseEnter,
    onMouseLeave,
  } = useNotificationMenu(notification.id)

  return (
    <NotificationContainer
      isUnread={isUnread}
      notificationUrl={notificationUrl}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <NotificationaUserAvatars involvedUsers={[triggerUser]} />
      <div
        className='tw-flex tw-flex-col tw-gap-2 tw-grow'
        data-testid={`notification-item`}
      >
        <div className='tw-flex tw-gap-2'>
          <div className='tw-break-inner tw-text-sm tw-grow'>
            {isLoadingRoomInfo || !roomInfo ? (
              <div className='tw-font-medium tw-h-6 tw-animate-skeleton-shimmer' />
            ) : (
              <NotificationContent
                removedUserUuid={triggerUser}
                crunchName={roomInfo.crunchName}
                checklistName={roomInfo.checklistName}
                sectionName={sectionName}
                isUnassigned={isUserUnassignedCustomNotification(notification)}
              />
            )}
          </div>
          <NotificationMenu
            notificationId={notification.id}
            isUnread={isUnread}
            isHovered={isHovered}
            isMenuOpened={isMenuOpened}
            setIsMenuOpened={setIsMenuOpened}
            date={notification.notifiedAt}
            onMarkAsRead={onMarkAsRead}
            onDelete={onDelete}
          />
        </div>
      </div>
    </NotificationContainer>
  )
}
